/**
 * Maintenance request list — `GET maintenance/`, scoped to the caller by the
 * backend (a tenant sees only their own requests; managers see whatever
 * `buildScope` grants them). Status/priority filters are server-side.
 *
 * Unlike every other list page in this app, there is no role gate on who may
 * see this page or file a new request — `maintenance/routes.ts` uses plain
 * `IsAuthenticated` on the whole CRUD surface, including a tenant on their own
 * requests (see the file's top comment).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Column, Grid, InlineLoading, Select, SelectItem } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { Wrench } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { EmptyState } from '../../components/EmptyState';
import { Notify } from '../../components/carbon/Notify';
import { Pager } from '../../components/carbon/Pager';
import { PageHeader } from '../../components/carbon/PageHeader';
import { StatusTag } from '../../components/carbon/StatusTag';
import {
  ResponsiveDataTable,
  type Column as DataColumn,
} from '../../components/carbon/ResponsiveDataTable';
import { listMaintenanceRequests } from '../../api/endpoints/maintenance';
import { REQUEST_PRIORITIES, REQUEST_STATUSES } from '../../types/maintenance';
import type { MaintenanceRequestRow, RequestPriority, RequestStatus } from '../../types/maintenance';

export function MaintenanceListPage(): React.JSX.Element {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RequestStatus | ''>('');
  const [priority, setPriority] = useState<RequestPriority | ''>('');

  const query = useQuery({
    queryKey: ['maintenance-requests', { page, status, priority }],
    queryFn: () =>
      listMaintenanceRequests({
        page,
        status: status === '' ? undefined : status,
        priority: priority === '' ? undefined : priority,
      }),
  });

  const requests = query.data?.results ?? [];
  const count = query.data?.count ?? 0;

  const columns: readonly DataColumn<MaintenanceRequestRow>[] = [
    { key: 'title', header: 'Title', render: (row) => <Link to={`${row.id}`}>{row.title}</Link> },
    { key: 'tenant_name', header: 'Tenant', render: (row) => row.tenant_name },
    { key: 'unit_number', header: 'Unit', render: (row) => row.unit_number },
    { key: 'property_name', header: 'Property', render: (row) => row.property_name },
    { key: 'category', header: 'Category', render: (row) => row.category },
    { key: 'priority', header: 'Priority', render: (row) => <StatusTag status={row.priority} /> },
    { key: 'status', header: 'Status', render: (row) => <StatusTag status={row.status} /> },
    { key: 'created_at', header: 'Filed', render: (row) => row.created_at },
  ];

  return (
    <section>
      <PageHeader
        title="Maintenance requests"
        actions={
          <Button as={Link} to="new" renderIcon={Add}>
            File a request
          </Button>
        }
      />

      <Grid
        className="filter-grid"
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <Column sm={4} md={3} lg={5}>
          <Select
            id="maintenance-status"
            labelText="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as RequestStatus | '');
              setPage(1);
            }}
          >
            <SelectItem value="" text="All" />
            {REQUEST_STATUSES.map((value) => (
              <SelectItem key={value} value={value} text={value} />
            ))}
          </Select>
        </Column>
        <Column sm={4} md={3} lg={5}>
          <Select
            id="maintenance-priority"
            labelText="Priority"
            value={priority}
            onChange={(event) => {
              setPriority(event.target.value as RequestPriority | '');
              setPage(1);
            }}
          >
            <SelectItem value="" text="All" />
            {REQUEST_PRIORITIES.map((value) => (
              <SelectItem key={value} value={value} text={value} />
            ))}
          </Select>
        </Column>
        <Column sm={4} md={2} lg={2} className="filter-grid__submit">
          <Button type="submit" kind="secondary">
            Apply
          </Button>
        </Column>
      </Grid>

      {query.isPending && <InlineLoading description="Loading maintenance requests…" />}
      {query.isError && <Notify kind="error" title={parseApiError(query.error).message} />}

      {query.isSuccess && requests.length === 0 && (
        <EmptyState
          icon={Wrench}
          message="No maintenance requests found."
          action={{ label: 'File a request', to: 'new' }}
        />
      )}

      {query.isSuccess && requests.length > 0 && (
        <>
          <ResponsiveDataTable
            caption="Maintenance requests"
            columns={columns}
            rows={requests}
            rowId={(row) => String(row.id)}
          />
          <Pager page={page} count={count} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}

export default MaintenanceListPage;
